import { normalize, schema } from 'normalizr';
import { createLogic } from 'redux-logic';
import { filter, isArray, includes, intersection, map, maxBy } from 'lodash';
import { normalizePagination } from 'utils';

// Action Types
const FETCH = '@@app/favourites/FETCH';
const FETCH_SUCCESS = '@@app/favourites/FETCH_SUCCESS';

const FETCH_IDS = '@@app/favourites/FETCH_IDS';
const FETCH_IDS_SUCCESS = '@@app/favourites/FETCH_IDS_SUCCESS';

const POST = '@@app/favourites/POST';
const POST_SUCCESS = '@@app/favourites/POST_SUCCESS';

const DELETE = '@@app/favourites/DELETE';
const DELETE_SUCCESS = '@@app/favourites/DELETE_SUCCESS';

const INIT = '@@app/favourites/INIT';
const ERROR = '@@app/favourites/ERROR';
const CANCEL = '@@app/favourites/CANCEL';

// Initial State
export const initialState = {
  isLoading: false,
  entities: {
    PlaylistVideo: {},
    recipes: {},
    workouts: {},
  },
  ids: {
    PlaylistVideo: [],
    recipes: [],
    workouts: [],
  },
  pagination: {
    PlaylistVideo: {},
    recipes: {},
    workouts: {},
  },
};

// Observables
const deleteLogic = createLogic({
  type: DELETE,
  processOptions: {
    successType: DELETE_SUCCESS,
    failType: ERROR,
  },
  process({ action, httpClient }) {
    const { memberId, params } = action.payload;
    const { itemId, itemType } = params;

    // Hack till API is updated for workouts & recipes
    const endpoint =
      itemType === 'PlaylistVideo'
        ? `/v1/voome/members/${memberId}/favourites/undo`
        : `/v1/members/${memberId}/favourite_${itemType}/${itemId}`;

    return httpClient.delete(endpoint, { params }).then(() => params);
  },
});

const fetchIdsLogic = createLogic({
  type: FETCH_IDS,
  processOptions: {
    successType: FETCH_IDS_SUCCESS,
    failType: ERROR,
  },
  process({ action, httpClient }) {
    const { memberId, params } = action.payload;
    const { itemType } = params;

    // Hack till API is updated for workouts & recipes
    const endpoint =
      itemType === 'PlaylistVideo'
        ? `/v1/voome/members/${memberId}/favourites/ids`
        : `/v1/members/${memberId}/favourite_${itemType}/ids`;

    return httpClient
      .get(endpoint, { params })
      .then(({ data }) => ({ ...params, data }));
  },
});

const fetchLogic = createLogic({
  type: FETCH,
  processOptions: {
    successType: FETCH_SUCCESS,
    failType: ERROR,
  },
  process({ action, httpClient }) {
    const { memberId, params } = action.payload;
    const { itemType, level, order, page } = params;

    const onSuccess = ({ data, pagination }) => {
      // Hack till API is updated for workouts & recipes
      const legacyData =
        itemType === 'recipes'
          ? map(data, recipeVariations => maxBy(recipeVariations, 'serves'))
          : data;

      const normalizedPagination = normalizePagination({
        pagination,
        data: legacyData,
        key: itemType,
        idAttribute: paginationKey(params),
      });

      return {
        itemType,
        ...normalizedPagination,
      };
    };

    // Hack till API is updated for workouts & recipes
    const endpoint =
      itemType === 'PlaylistVideo'
        ? `/v1/voome/members/${memberId}/favourites`
        : `/v1/members/${memberId}/favourite_${itemType}`;

    // Hack till API is updated for workouts & recipes
    const legacyParams =
      itemType === 'PlaylistVideo'
        ? params
        : itemType === 'recipes' ? { level, order, page } : { order, page };

    return httpClient.get(endpoint, { params: legacyParams }).then(onSuccess);
  },
});

const initLogic = createLogic({
  type: INIT,
  process({ action, httpClient }, dispatch, done) {
    const { memberId } = action.payload;

    dispatch(fetchFavouriteVideoIds(memberId));
    dispatch(fetchFavouriteRecipeIds(memberId));
    dispatch(fetchFavouriteWorkoutIds(memberId));

    done();
  },
});

const postLogic = createLogic({
  type: POST,
  processOptions: {
    successType: POST_SUCCESS,
    failType: ERROR,
  },
  process({ action, httpClient }) {
    const { memberId, params } = action.payload;
    const { itemId, itemType } = params;

    // Hack till API is updated for workouts & recipes
    const endpoint =
      itemType === 'PlaylistVideo'
        ? `/v1/voome/members/${memberId}/favourites`
        : `/v1/members/${memberId}/favourite_${itemType}`;

    // Hack till API is updated for workouts & recipes
    const legacyParams =
      itemType === 'PlaylistVideo'
        ? params
        : itemType === 'recipes'
          ? { memberId, recipeId: itemId }
          : { memberId, exercisePlanDayId: itemId };

    return httpClient.post(endpoint, legacyParams).then(() => params);
  },
});

export const favouriteLogic = [
  deleteLogic,
  fetchLogic,
  fetchIdsLogic,
  initLogic,
  postLogic,
];

// Action Creators
const createFavourite = ({ itemId, itemType, memberId }) => {
  return {
    type: POST,
    payload: {
      memberId,
      params: { itemId, itemType },
    },
  };
};

const deleteFavourite = ({ itemId, itemType, memberId }) => {
  return {
    type: DELETE,
    payload: {
      memberId,
      params: { itemId, itemType },
    },
  };
};

const fetchFavourites = ({ itemType, level, memberId, order, page = 1 }) => {
  return {
    type: FETCH,
    payload: {
      memberId,
      params: { itemType, level, order, page },
    },
  };
};

const fetchFavouriteIds = ({ itemType, memberId }) => {
  return {
    type: FETCH_IDS,
    payload: {
      memberId,
      params: { itemType },
    },
  };
};

const toggleFavourite = params => {
  return params.isFavourite ? deleteFavourite(params) : createFavourite(params);
};

export const initFavourites = memberId => {
  return { type: INIT, payload: { memberId } };
};

export const fetchFavouriteRecipeIds = memberId => {
  return fetchFavouriteIds({ memberId, itemType: 'recipes' });
};

export const fetchFavouriteVideoIds = memberId => {
  return fetchFavouriteIds({ memberId, itemType: 'PlaylistVideo' });
};

export const fetchFavouriteWorkoutIds = memberId => {
  return fetchFavouriteIds({ memberId, itemType: 'workouts' });
};

export const fetchFavouriteRecipes = params => {
  return fetchFavourites({ ...params, itemType: 'recipes' });
};

export const fetchFavouriteVideos = params => {
  return fetchFavourites({ ...params, itemType: 'PlaylistVideo' });
};

export const fetchFavouriteWorkouts = params => {
  return fetchFavourites({ ...params, itemType: 'workouts' });
};

export const toggleFavouriteRecipe = params => {
  const { memberId, isFavourite, recipeId } = params;

  return toggleFavourite({
    isFavourite,
    memberId,
    itemId: recipeId,
    itemType: 'recipes',
  });
};

export const toggleFavouriteVideo = params => {
  const { memberId, isFavourite, videoId } = params;

  return toggleFavourite({
    isFavourite,
    memberId,
    itemId: videoId,
    itemType: 'PlaylistVideo',
  });
};

export const toggleFavouriteWorkout = params => {
  const { memberId, isFavourite, workoutId } = params;

  return toggleFavourite({
    isFavourite,
    memberId,
    itemId: workoutId,
    itemType: 'workouts',
  });
};

// Helpers
const paginationKey = params => {
  // Ensure the pagination idAttribute is always the same.
  const { level, order, page = 1 } = params;
  const isDefined = v => v !== undefined && v !== null && v !== '';
  return filter([page, level, order], isDefined).join('-');
};

const isFavourite = (data, ids = []) => {
  return data
    ? {
        ...data,
        isFavourite: includes(ids, data.id),
      }
    : undefined;
};

const mapIsFavourite = (data, ids = [], key) => {
  const mapItems = items => map(items, item => isFavourite(item, ids));

  return data
    ? isArray(data)
      ? mapItems(data)
      : data[key]
        ? { ...data, [key]: mapItems(data[key]) }
        : isFavourite(data, ids)
    : undefined;
};

export const mapIsFavouriteWorkouts = (data, ids = []) => {
  return mapIsFavourite(data, ids, 'workouts');
};

export const mapIsFavouriteRecipes = (data, ids = []) => {
  return mapIsFavourite(data, ids, 'recipes');
};

export const mapIsFavouriteVideos = (data, ids = []) => {
  return mapIsFavourite(data, ids, 'PlaylistVideo');
};

// Selectors
export const selectFavourites = (state, ids, params) => {
  const { itemType } = params;
  const key = paginationKey(params);
  const pagination = state.pagination[itemType][key];

  return pagination
    ? {
        ...pagination,
        result: map(pagination.result, id =>
          isFavourite(state.entities[params.itemType][id], ids)
        ),
      }
    : {};
};

export const selectShouldReload = (state, result, key) => {
  return (
    result === undefined ||
    (state.isLoading === false && state.pagination[key].reload === true)
  );
};

export const selectFavouriteRecipeIds = state => {
  return state.ids.recipes;
};

export const selectFavouriteRecipes = (state, params) => {
  const itemType = 'recipes';
  const ids = selectFavouriteRecipeIds(state);
  return selectFavourites(state, ids, { ...params, itemType });
};

export const selectFavouriteRecipesReload = (state, result) => {
  return selectShouldReload(state, result, 'recipes');
};

export const selectFavouriteVideoIds = state => {
  return state.ids.PlaylistVideo;
};

export const selectFavouriteVideos = (state, params) => {
  const itemType = 'PlaylistVideo';
  const ids = selectFavouriteVideoIds(state);
  return selectFavourites(state, ids, { ...params, itemType });
};

export const selectFavouriteVideosReload = (state, result) => {
  return selectShouldReload(state, result, 'PlaylistVideo');
};

export const selectFavouriteWorkoutIds = state => {
  return state.ids.workouts;
};

export const selectFavouriteWorkouts = (state, params) => {
  const itemType = 'workouts';
  const ids = selectFavouriteWorkoutIds(state);
  return selectFavourites(state, ids, { ...params, itemType });
};

export const selectFavouriteWorkoutsReload = (state, result) => {
  return selectShouldReload(state, result, 'workouts');
};

// Reducer
export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case INIT:
    case FETCH:
    case FETCH_IDS:
      return {
        ...state,
        isLoading: true,
      };

    case FETCH_SUCCESS:
      return {
        ...state,
        isLoading: false,
        entities: {
          ...state.entities,
          [payload.itemType]: {
            ...state.entities[payload.itemType],
            ...payload.entities[payload.itemType],
          },
        },
        pagination: {
          ...state.pagination,
          [payload.itemType]: {
            ...state.pagination[payload.itemType],
            ...payload.entities.pagination,
          },
        },
      };

    case FETCH_IDS_SUCCESS:
      return {
        ...state,
        isLoading: false,
        ids: {
          ...state.ids,
          [payload.itemType]: payload.data,
        },
      };

    case POST_SUCCESS:
      return {
        ...state,
        isLoading: false,
        ids: {
          ...state.ids,
          [payload.itemType]: [...state.ids[payload.itemType], payload.itemId],
        },
        pagination: {
          ...state.pagination,
          [payload.itemType]: {
            ...state.pagination[payload.itemType],
            reload: true,
          },
        },
      };

    case DELETE_SUCCESS:
      return {
        ...state,
        isLoading: false,
        ids: {
          ...state.ids,
          [payload.itemType]: state.ids[payload.itemType].filter(
            id => id !== payload.itemId
          ),
        },
        pagination: {
          ...state.pagination,
          [payload.itemType]: {
            ...state.pagination[payload.itemType],
            reload: true,
          },
        },
      };

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/logic/favourite.js